import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { redirectLegacyPortalHost } from "./lib/legacy-host-redirect.js";
import { portalSignInPath } from "./lib/portal-tenant-path.js";
import { queryClient, setAuthExpiredHandler } from "./lib/query-client.js";
import { rootRoute } from "./routes/__root.js";
import { portalBoardRoute } from "./routes/_portal/board.js";
import { portalCalendarRoute } from "./routes/_portal/calendar.js";
import { portalIndexRoute } from "./routes/_portal/index.js";
import { portalPlanRoute } from "./routes/_portal/plan.js";
import { portalProgrammesRoute, portalProgrammeDetailRoute } from "./routes/_portal/programmes.js";
import { portalProjectPlanRoute } from "./routes/_portal/project-plan.js";
import { portalPublishedRoute } from "./routes/_portal/published.js";
import { portalReviewsRoute } from "./routes/_portal/reviews.js";
import { portalVaultRoute } from "./routes/_portal/vault.js";
import { portalWorkingGroupsRoute } from "./routes/_portal/working-groups.js";
import { portalRoute } from "./routes/_portal.js";
import { portalAuthErrorRoute } from "./routes/portal-public/auth-error.js";
import { portalSignInRoute } from "./routes/portal-public/sign-in.js";
import { portalWelcomeRoute } from "./routes/portal-public/welcome.js";
import { legacyPortalRedirectRoute, rootEntryRoute } from "./routes/root-entry.js";
import { tenantScopeRoute } from "./routes/tenant-scope.js";

import "./styles/globals.css";

/**
 * Portal SPA bootstrap.
 *
 * Distinct from the staff `main.tsx` because:
 *   - No Clerk provider — the portal auth is a magic-link cookie
 *     set by the API, read by `portalFetch` via `credentials: include`.
 *   - No `setAuthTokenGetter` bridge — there's no Clerk JWT to bridge.
 *   - No Sonner toaster — portal failures surface as inline ErrorState
 *     components, quieter UX for a customer-facing surface.
 *
 * Phase 1 route tree: family hosts + `/t/$tenantSlug/…` path tenant.
 * Apex `/` and legacy `/portal/…` redirect into the scoped tree.
 *
 * Canon: docs/enhancements/portal-swa-split-2026-05.md,
 * docs/ops/partner-portal-swa-bind.md.
 */

const routeTree = rootRoute.addChildren([
  rootEntryRoute,
  legacyPortalRedirectRoute,
  tenantScopeRoute.addChildren([
    portalWelcomeRoute,
    portalSignInRoute,
    portalAuthErrorRoute,
    portalRoute.addChildren([
      portalIndexRoute,
      portalProgrammesRoute,
      portalProgrammeDetailRoute,
      portalPlanRoute,
      portalVaultRoute,
      portalProjectPlanRoute,
      portalPublishedRoute,
      portalReviewsRoute,
      portalCalendarRoute,
      portalBoardRoute,
      portalWorkingGroupsRoute,
    ]),
  ]),
]);

// Prefetch route code + loader on link hover/focus (assessment U5).
const router = createRouter({ routeTree, defaultPreload: "intent" });

// Route an expired/invalid portal session to sign-in from anywhere the
// QueryCache sees a 401/403 (assessment U1). Guard against redundant
// navigations when the user is already on a public (unauthenticated)
// route so we don't loop.
setAuthExpiredHandler(() => {
  const signIn = portalSignInPath();
  if (router.state.location.pathname === signIn) return;
  if (router.state.location.pathname.endsWith("/portal/sign-in")) return;
  void router.navigate({ href: signIn, replace: true });
});

const root = document.getElementById("root")!;

// Retired family host → canonical root, before anything renders, so a
// bookmarked legacy URL never flashes a half-mounted portal. Inert until
// the build is configured for the canonical root.
if (!redirectLegacyPortalHost()) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
